import axios from 'axios';
import axiosRetry from 'axios-retry';
import { isFuture } from 'date-fns';
import jwtDecode from 'jwt-decode';
import { getCookie, setCookieDays, setCookieSeconds } from 'utils/cookies';
import { getConfig } from 'config/runtime';

const BASE_URL = getConfig('REACT_APP_URL_API');
const API_VERSION = getConfig('REACT_APP_API_VERSION');

// Create axios instance
const axiosInstance = axios.create({
  baseURL: `${BASE_URL}/${API_VERSION}`,
  timeout: 10000,
});

const retryConditionHandler = (error) => {
  // Retry on network errors, 5xx errors, or timeout errors
  const isNetworkOrIdempotent = axiosRetry.isNetworkOrIdempotentRequestError(error);
  const isTimeoutError = error.code === 'ECONNABORTED' || error.message.includes('timeout');
  return isNetworkOrIdempotent || isTimeoutError;
};

axiosRetry(axiosInstance, {
  retries: 3,
  retryDelay: axiosRetry.exponentialDelay,
  shouldResetTimeout: true,
  retryCondition: retryConditionHandler,
  onRetry: (retryCount, error) => {
    // eslint-disable-next-line no-console
    console.log(`Retry attempt #${retryCount}. Error: ${error.message}`);
  },
});

// Toast function holder - will be injected from React context
let showToastFn = null;

// Setter for toast function (called from AppProvider)
export const setAxiosToastHandler = (toastFn) => {
  showToastFn = toastFn;
};

// Token refresh state to prevent multiple refresh calls
let isRefreshing = false;
let failedQueue = [];

// Helper function to clear auth tokens
const clearAuthTokens = () => {
  setCookieSeconds('grbut', '', 1);
  setCookieSeconds('grbrt', '', 1);
};

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Request interceptor
axiosInstance.interceptors.request.use(
  async (config) => {
    config.headers['app-currency'] = 'IDR';

    const accessToken = getCookie('grbut');
    const refreshTokenValue = getCookie('grbrt');

    if (!accessToken) return config;

    try {
      const { exp } = jwtDecode(accessToken);
      const expires = new Date(exp * 1000);

      const isAccessValid = isFuture(expires);

      if (!isAccessValid && refreshTokenValue) {
        if (!isRefreshing) {
          isRefreshing = true;

          try {
            const response = await axios.post(
              `${BASE_URL}/${API_VERSION}/authorization/refresh`,
              {
                refresh_token: refreshTokenValue,
              },
              {
                timeout: 10000,
                'axios-retry': {
                  retries: 3,
                  retryDelay: axiosRetry.exponentialDelay,
                  shouldResetTimeout: true,
                  retryCondition: retryConditionHandler,
                },
              },
            );

            const { access_token, refresh_token } = response.data;

            if (!access_token || !refresh_token) {
              throw new Error('Invalid refresh response');
            }

            setCookieDays('grbut', access_token, 1);
            setCookieDays('grbrt', refresh_token, 1);

            processQueue(null, access_token);
            isRefreshing = false;

            config.headers.Authorization = `Bearer ${access_token}`;
            return config;
          } catch (err) {
            processQueue(err, null);
            isRefreshing = false;
            clearAuthTokens();
            window.location.href = '/login';
            return Promise.reject(err);
          }
        }

        // Wait if already refreshing
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          config.headers.Authorization = `Bearer ${token}`;
          return config;
        });
      }

      config.headers.Authorization = `Bearer ${accessToken}`;
      return config;
    } catch (err) {
      clearAuthTokens();
      window.location.href = '/login';
      return Promise.reject(err);
    }
  },
  (error) => Promise.reject(error),
);

// Response interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle network errors
    if (!error.response) {
      const message = 'Network error. Please check your connection.';
      showToastFn?.({ type: 'error', message });
      return Promise.reject(error);
    }

    const { status, data } = error.response;

    // Handle 401 Unauthorized - attempt token refresh
    // Skip refresh for auth endpoints (login, refresh, destroy) to avoid loops
    const isAuthEndpoint = originalRequest.url?.includes('/authorization');
    if (status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      if (isRefreshing) {
        // Queue the request while refresh is in progress
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return axiosInstance(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshTokenValue = getCookie('grbrt');

      if (!refreshTokenValue) {
        // No refresh token, clear auth and redirect to login
        isRefreshing = false;
        processQueue(error, null);
        clearAuthTokens();
        showToastFn?.({
          type: 'error',
          message: 'Session expired. Please login again.',
        });
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        const response = await axios.post(
          `${BASE_URL}/${API_VERSION}/authorization/refresh`,
          {
            refresh_token: refreshTokenValue,
          },
          {
            timeout: 10000,
            'axios-retry': {
              retries: 3,
              retryDelay: axiosRetry.exponentialDelay,
              shouldResetTimeout: true,
              retryCondition: retryConditionHandler,
            },
          },
        );

        const { access_token, refresh_token } = response.data;

        if (!access_token || !refresh_token) {
          throw new Error('Invalid token response from server');
        }

        setCookieDays('grbut', access_token, 1);
        setCookieDays('grbrt', refresh_token, 1);

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
        }

        processQueue(null, access_token);
        isRefreshing = false;

        return axiosInstance(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        isRefreshing = false;
        clearAuthTokens();
        showToastFn?.({
          type: 'error',
          message: 'Session expired. Please login again.',
        });
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    // Handle other error status codes
    const errorMessage = getErrorMessage(status, data);

    // Only show toast for specific error types that aren't handled by the component
    if (shouldShowToast(status, originalRequest)) {
      showToastFn?.({ type: 'error', message: errorMessage });
    }

    return Promise.reject(error);
  },
);

// Helper function to get error message based on status code
const getErrorMessage = (status, data) => {
  // Use server-provided message if available
  if (data?.message) {
    return data.message;
  }

  // Default messages for common status codes
  switch (status) {
    case 400:
      return 'Invalid request. Please check your input.';
    case 401:
      return 'Authentication required. Please login.';
    case 403:
      return 'Access denied. You do not have permission.';
    case 404:
      return 'Resource not found.';
    case 409:
      return 'Conflict. The resource already exists.';
    case 422:
      return 'Validation error. Please check your input.';
    case 429:
      return 'Too many requests. Please try again later.';
    case 500:
      return 'Server error. Please try again later.';
    case 502:
      return 'Service temporarily unavailable.';
    case 503:
      return 'Service unavailable. Please try again later.';
    default:
      return 'Something went wrong. Please try again.';
  }
};

// Helper function to determine if toast should be shown
// Some errors are handled by components, so we don't want to show duplicate toasts
const shouldShowToast = (status, config) => {
  // Don't show toast for auth endpoints (they handle their own errors)
  if (config.url?.includes('/authorization')) {
    return false;
  }

  // Don't show toast for validation errors (usually handled by forms)
  if (status === 422 || status === 400) {
    return false;
  }

  // Show toast for server errors and other critical errors
  return status >= 500 || status === 403 || status === 429;
};

export default axiosInstance;
